// apps/admin/src/types/finance.ts
//
// Phase 8.2 Wave 1 Task 04 — Finance domain type surface for the admin
// portal. Single source of truth shared across the Billing tabs, Payouts
// page, and Transactions page.
//
// Per CONTEXT.md
//   • Billing page tabs (Topup Reviews / Credits / Gateways).
//   • Payouts page (vendor payout state machine + KYC gate).
//   • Transactions page (cross-merchant/vendor txn ledger).
//   • Merchant credit schema is versioned (effective_date +
//                previous_version_id).
//   • Topup review UI branches on `verification_status`
//                (auto_verified inline → manual_review modal).
//
// Per INTERFACE-STANDARDS.md §money — all monetary amounts use the
// `usdCents: number` discipline. Wire payloads use snake_case (e.g.
// `amount_cents`); the TS types mirror the wire shape directly via
// `z.infer<...>` to avoid silent rename drift. Display components
// (e.g. <Money>) MUST treat every cents field as USD cents.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Topup Reviews — GET /api/v1/admin/finance/billing/topup-reviews
// ---------------------------------------------------------------------------

export const TopupReviewVerificationStatusSchema = z.enum([
  'auto_verified',
  'manual_review',
]);
export type TopupReviewVerificationStatus = z.infer<
  typeof TopupReviewVerificationStatusSchema
>;

export const TopupReviewStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
]);
export type TopupReviewStatus = z.infer<typeof TopupReviewStatusSchema>;

export const TopupReviewSlaBadgeSchema = z.enum(['green', 'yellow', 'red']);
export type TopupReviewSlaBadge = z.infer<typeof TopupReviewSlaBadgeSchema>;

export const TopupReviewAgeBucketSchema = z.enum([
  'fresh',
  'yellow_2h',
  'red_4h',
  'all',
]);
export type TopupReviewAgeBucket = z.infer<typeof TopupReviewAgeBucketSchema>;

/** Single row in GET /admin/finance/billing/topup-reviews response. */
export const TopupReviewRowSchema = z.object({
  id: z.string(),
  merchant_id: z.string(),
  merchant_name: z.string(),
  // Owner email — BE surfaces it so the merchant column can show email
  // instead of the raw _id. Optional/nullable for resilience against any
  // cached row shape that predates the field.
  merchant_email: z.string().nullable().optional(),
  amount_cents: z.number().int(),
  currency: z.literal('USD'),
  verification_status: TopupReviewVerificationStatusSchema,
  memo: z.string().nullable(),
  // BE serialiser emits a relative API path (`/api/v1/admin/files/:id`) for
  // proof receipts, not an absolute URL. Accept either shape — `.url()` was
  // too strict and rejected the path form, dropping the whole page.
  proof_url: z.string().nullable(),
  imap_raw_preview: z.string().nullable(),
  status: TopupReviewStatusSchema,
  age_minutes: z.number().int(),
  sla_badge: TopupReviewSlaBadgeSchema,
  created_at: z.string().datetime(),
  // Admin who approved/rejected — powers the "Người duyệt" column. Null for
  // pending rows (not yet reviewed).
  reviewed_by: z.string().nullable().optional(),
  reviewed_by_email: z.string().nullable().optional(),
  // Reason captured when a topup is rejected — powers the "Lý do từ chối"
  // column. Null for pending/approved rows.
  reject_reason: z.string().nullable().optional(),
});
export type TopupReviewRow = z.infer<typeof TopupReviewRowSchema>;

export const TopupReviewMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  overdue_count: z.number().int(),
});
export type TopupReviewMeta = z.infer<typeof TopupReviewMetaSchema>;

/** Filter input for listTopupReviews. snake_case mirrors wire query keys. */
export interface TopupReviewListFilters {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'rejected' | 'all';
  verification_status?: 'auto_verified' | 'manual_review' | 'all';
  merchant_id?: string;
  age_bucket?: TopupReviewAgeBucket;
  sort?: 'created_at' | '-created_at' | 'amount' | '-amount';
}

/** Unwrapped list payload (envelope already stripped by apiClient). */
export interface TopupReviewListPayload {
  rows: TopupReviewRow[];
  meta: TopupReviewMeta;
}

// ---------------------------------------------------------------------------
// Credits — GET /api/v1/admin/finance/credits  (cross-merchant)
// ---------------------------------------------------------------------------

export const FinanceCreditStatusSchema = z.enum([
  'active',
  'used',
  'expired',
  'revoked',
]);
export type FinanceCreditStatus = z.infer<typeof FinanceCreditStatusSchema>;

export const FinanceCreditRowSchema = z.object({
  id: z.string(),
  merchant_id: z.string(),
  merchant_name: z.string(),
  amount_cents: z.number().int(),
  used_cents: z.number().int(),
  remaining_cents: z.number().int(),
  expires_at: z.string().datetime().nullable(),
  reason: z.string(),
  created_by: z.string(),
  /** Versioned. */
  version: z.number().int(),
  effective_date: z.string().datetime(),
  previous_version_id: z.string().nullable(),
  status: FinanceCreditStatusSchema,
});
export type FinanceCreditRow = z.infer<typeof FinanceCreditRowSchema>;

export const FinanceCreditMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
});
export type FinanceCreditMeta = z.infer<typeof FinanceCreditMetaSchema>;

export interface FinanceCreditListFilters {
  page?: number;
  limit?: number;
  merchant_id?: string;
  status?: 'active' | 'used' | 'expired' | 'revoked' | 'all';
  expires_within_days?: number;
  sort?: 'created_at' | '-created_at' | 'expires_at' | '-expires_at';
}

export interface FinanceCreditListPayload {
  rows: FinanceCreditRow[];
  meta: FinanceCreditMeta;
}

// ---------------------------------------------------------------------------
// Payouts — GET /api/v1/admin/payouts
// ---------------------------------------------------------------------------

export const PayoutStatusSchema = z.enum([
  'Pending',
  'Processing',
  'Completed',
  'Failed',
]);
export type PayoutStatus = z.infer<typeof PayoutStatusSchema>;

export const PayoutKycStatusSchema = z.enum([
  'approved',
  'pending',
  'rejected',
]);
export type PayoutKycStatus = z.infer<typeof PayoutKycStatusSchema>;

export const PayoutRowSchema = z.object({
  id: z.string(),
  vendor_id: z.string(),
  vendor_name: z.string(),
  amount_cents: z.number().int(),
  fee_cents: z.number().int(),
  net_cents: z.number().int(),
  status: PayoutStatusSchema,
  kyc_status: PayoutKycStatusSchema,
  gateway_code: z.string(),
  created_at: z.string().datetime(),
  failure_reason: z.string().nullable(),
});
export type PayoutRow = z.infer<typeof PayoutRowSchema>;

export const PayoutSummarySchema = z.object({
  total_pending_cents: z.number().int(),
  approved_this_month_cents: z.number().int(),
});
export type PayoutSummary = z.infer<typeof PayoutSummarySchema>;

export const PayoutMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  summary: PayoutSummarySchema,
});
export type PayoutMeta = z.infer<typeof PayoutMetaSchema>;

export interface PayoutListFilters {
  page?: number;
  limit?: number;
  status?:
    | 'Pending'
    | 'Processing'
    | 'Completed'
    | 'Failed'
    | 'PreKycBlocked'
    | 'all';
  vendor_id?: string;
  date_from?: string;
  date_to?: string;
  sort?: 'created_at' | '-created_at' | 'amount' | '-amount';
}

export interface PayoutListPayload {
  rows: PayoutRow[];
  meta: PayoutMeta;
}

// ---------------------------------------------------------------------------
// Transactions — GET /api/v1/admin/transactions
// ---------------------------------------------------------------------------

export const TransactionTypeSchema = z.enum([
  'topup_credit',
  'order_charge',
  'refund_debit',
  'credit_grant',
  'credit_use',
  'payout_debit',
  'gateway_fee',
  'manual_adjustment',
  'invoice_payment',
  'credit_memo_apply',
  'pre_payment',
  'transfer_in',
  'transfer_out',
  'chargeback',
]);
export type TransactionType = z.infer<typeof TransactionTypeSchema>;

export const TransactionDirectionSchema = z.enum(['in', 'out']);
export type TransactionDirection = z.infer<typeof TransactionDirectionSchema>;

export const TransactionStatusSchema = z.enum([
  'pending',
  'completed',
  'failed',
]);
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>;

export const TransactionRowSchema = z.object({
  id: z.string(),
  type: TransactionTypeSchema,
  direction: TransactionDirectionSchema,
  amount_cents: z.number().int(),
  currency: z.literal('USD'),
  merchant_id: z.string().nullable(),
  // Phase 8.2 debug Cluster C view #4 — BE now resolves owner-derived
  // merchant display name + vendor name + invoice_number; plus surfaces
  // tx_group metadata.orderId / creditMemoId so FE can render the short
  // ORD-/CM- ids instead of raw 24-char hex. All optional/nullable so
  // legacy callers continue to validate.
  merchant_name: z.string().nullable().optional(),
  vendor_id: z.string().nullable(),
  vendor_name: z.string().nullable().optional(),
  related_invoice_id: z.string().nullable(),
  related_invoice_number: z.string().nullable().optional(),
  related_order_id: z.string().nullable().optional(),
  related_credit_memo_id: z.string().nullable().optional(),
  description: z.string(),
  status: TransactionStatusSchema,
  created_at: z.string().datetime(),
});
export type TransactionRow = z.infer<typeof TransactionRowSchema>;

export const TransactionSummarySchema = z.object({
  total_in_cents: z.number().int(),
  total_out_cents: z.number().int(),
  net_cents: z.number().int(),
  pending_count: z.number().int(),
});
export type TransactionSummary = z.infer<typeof TransactionSummarySchema>;

export const TransactionMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  summary: TransactionSummarySchema,
});
export type TransactionMeta = z.infer<typeof TransactionMetaSchema>;

export interface TransactionListFilters {
  page?: number;
  limit?: number;
  type?: TransactionType[];
  merchant_id?: string;
  vendor_id?: string;
  date_from?: string;
  date_to?: string;
  status?: 'pending' | 'completed' | 'failed' | 'all';
  /**
   *  (§6.27 Transactions): cross-link filter by source
   * order or by source adjustment (manual_adjustment / credit_memo_apply
   * etc.). Wire keys are snake_case.
   */
  linked_order_id?: string;
  linked_adjustment_id?: string;
  sort?: 'created_at' | '-created_at';
}

export interface TransactionListPayload {
  rows: TransactionRow[];
  meta: TransactionMeta;
}

// ---------------------------------------------------------------------------
// Admin Invoices — GET /api/v1/admin/invoices
//                  POST /api/v1/admin/invoices/resend-email
//
// Phase 8.2 Wave 3 Task 11 — Invoice list A1 (cross-merchant + bulk
// resend). Contract mirror of
//   .vg/phases/08.2-admin-finance-catalog/API-CONTRACTS/get-api-v1-admin-invoices.md
//   .vg/phases/08.2-admin-finance-catalog/API-CONTRACTS/post-api-v1-admin-invoices-resend-email.md
// per Decisions A1, (numbering)
// (invoice immutability — no edit/delete in UI).
// ---------------------------------------------------------------------------

/**
 * Invoice lifecycle enum per Phase 8.2 contract `InvoiceStatus`.
 *
 *   DRAFT    — generated, not yet sent
 *   SENT     — emailed to merchant, awaiting payment
 *   PAID     — fully paid
 *   PARTIAL  — partially paid (sum(payments) < total)
 *   OVERDUE  — past due_at and not PAID
 *   VOID — voided / reversed (immutable — reversal recorded
 *              via separate credit-note doc; the original invoice's
 *              status flips to VOID but the doc itself is never deleted)
 *
 * NOTE: Phase 4.1 UI used a 7-status set (DRAFT/SENT/PENDING_APPROVAL/
 * PARTIAL/PAID/OVERDUE/VOIDED). Phase 8.2 contract narrows to 6 states
 * — PENDING_APPROVAL collapses into SENT (sent + awaiting auto-approval
 * is no longer a distinct UI badge A1 acceptance), and
 * VOIDED renames to VOID per the canonical enum.
 */
export const AdminInvoiceStatusSchema = z.enum([
  'DRAFT',
  'SENT',
  'PAID',
  'PARTIAL',
  'OVERDUE',
  'VOID',
]);
export type AdminInvoiceStatus = z.infer<typeof AdminInvoiceStatusSchema>;

/**
 * Single admin-side invoice row. Mirrors the GET /admin/invoices
 * response item — fields match the contract one-to-one.
 *
 * `invoice_number` is the human-readable prefix-coded identifier per
 *  (`INV-YYYYMM-XXXXX`, `MINV-YYYYMM-XXXXX`
 * `CN-YYYYMM-XXXXX`, `DN-YYYYMM-XXXXX`, `PR-YYYYMM-XXXXX`). The UI
 * derives the prefix badge from this string.
 *
 * `version` is the optimistic-concurrency token used by MarkPaid /
 * other mutation modals (task-13 / task-15). Surfaced here so the list
 * row can pass it through to the detail page without an extra fetch.
 */
export const AdminInvoiceRowSchema = z.object({
  id: z.string(),
  invoice_number: z.string(),
  merchant_id: z.string(),
  merchant_name: z.string(),
  merchant_email: z.string().nullable().optional(),
  legal_entity_id: z.string(),
  // Draft invoices haven't transitioned to issued yet → `issued_at` / `due_at`
  // are null. Phase 4.1 BE serialiser passes null through; the FE pill +
  // formatDate helpers already render '—' for null so the schema can stay
  // permissive without churning the render code.
  issued_at: z.string().datetime().nullable(),
  due_at: z.string().datetime().nullable(),
  period_from: z.string().datetime(),
  period_to: z.string().datetime(),
  subtotal_cents: z.number().int(),
  tax_cents: z.number().int(),
  total_cents: z.number().int(),
  paid_cents: z.number().int(),
  remaining_cents: z.number().int(),
  status: AdminInvoiceStatusSchema,
  is_manual: z.boolean(),
  version: z.number().int(),
  created_at: z.string().datetime(),
  // dbg-admin-invoices-cols-2026-05-23 — three new admin grid columns.
  invoice_type: z.enum(['Payment', 'Refund', 'Surcharge']).optional(),
  payment_gateway: z.string().nullable().optional(),
  payment_gateway_fee_cents: z.number().int().nullable().optional(),
});
export type AdminInvoiceRow = z.infer<typeof AdminInvoiceRowSchema>;

/**
 * `meta` envelope for GET /admin/invoices — page + overdue_count +
 * total_outstanding_cents summary surfaced by the header/CTA pills.
 */
export const AdminInvoiceListMetaSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  overdue_count: z.number().int(),
  total_outstanding_cents: z.number().int(),
});
export type AdminInvoiceListMeta = z.infer<typeof AdminInvoiceListMetaSchema>;

export interface AdminInvoiceListFilters {
  page?: number;
  limit?: number;
  merchant_id?: string;
  /** Multi-select per contract. Empty array == no filter. */
  status?: AdminInvoiceStatus[];
  period_from?: string;
  period_to?: string;
  overdue_only?: boolean;
  q?: string;
  sort?: 'issued_at' | '-issued_at' | 'due_at' | '-due_at' | 'amount' | '-amount';
}

export interface AdminInvoiceListPayload {
  rows: AdminInvoiceRow[];
  meta: AdminInvoiceListMeta;
}

/**
 * Bulk resend-email — POST /api/v1/admin/invoices/resend-email.
 *
 * Per contract: SINGLE backend call carrying every selected invoice id.
 * BE fans out enqueue jobs and replies with `queued_count` + per-id
 * `skipped[]` (reason = ineligible status / missing email / dup).
 */
export const BulkResendInvoiceEmailRequestSchema = z.object({
  invoice_ids: z
    .array(z.string().regex(/^[a-f0-9]{24}$/i))
    .min(1)
    .max(50),
  cc_emails: z.array(z.string().email()).max(5).optional(),
});
export type BulkResendInvoiceEmailRequest = z.infer<
  typeof BulkResendInvoiceEmailRequestSchema
>;

export const BulkResendInvoiceEmailResponseSchema = z.object({
  queued_count: z.number().int(),
  skipped: z.array(
    z.object({ invoice_id: z.string(), reason: z.string() }),
  ),
});
export type BulkResendInvoiceEmailResponse = z.infer<
  typeof BulkResendInvoiceEmailResponseSchema
>;
